// PASS 1: STRUCTURE EDIT (MACRO)
export const chapterChecklists = {
  fiction: [
    'Moves the plot forward',
    'Reveals or tests character',
    'Raises the stakes',
    'Introduces conflict or new information',
    'Deepens theme'
  ],
  nonfiction: [
    'Advances the core argument',
    'Supports the transformation journey',
    'Provides a key step or principle',
    'Offers essential context',
    'Prepares the reader for what\'s next'
  ]
};

export const purposeScores = [
  'Absolutely essential',
  'Helpful but could be clearer',
  'Probably needs reframing',
  'Might belong elsewhere',
  'Cut or combine'
];

export const macroChecklistData = [
  {
    id: 'alignment',
    title: '1. Alignment with Core Promise',
    items: [
      { id: 'm1-1', text: "Each chapter aligns with the book's purpose/theme" },
      { id: 'm1-2', text: "No chapters drift from the mission" },
      { id: 'm1-3', text: "The book consistently serves the target reader" }
    ]
  },
  {
    id: 'logical-flow',
    title: '2. Logical Flow',
    items: [
      { id: 'm2-1', text: "Chapters build naturally on each other" },
      { id: 'm2-2', text: "No confusing jumps in ideas or plot" },
      { id: 'm2-3', text: "Transitions are smooth and supportive" },
      { id: 'm2-4', text: "Order increases clarity and momentum" }
    ]
  },
  {
    id: 'chapter-purpose',
    title: '3. Chapter Purpose',
    items: [
      { id: 'm3-1', text: "Each chapter has a clear, single purpose" },
      { id: 'm3-2', text: "No chapter repeats the work of another" },
      { id: 'm3-3', text: "No chapter exists simply because it was written" }
    ]
  },
  {
    id: 'structure-length',
    title: '4. Structure & Length Balance',
    items: [
      { id: 'm4-1', text: "Chapters are generally consistent in length" },
      { id: 'm4-2', text: "No sections dominate disproportionately" },
      { id: 'm4-3', text: "Pacing feels intentional and varied" }
    ]
  },
  {
    id: 'gaps',
    title: '5. Gaps (Missing Material)',
    items: [
      { id: 'm5-1', text: "No missing explanations or contexts" },
      { id: 'm5-2', text: "Motivations are clear (fiction)" },
      { id: 'm5-3', text: "Steps or principles are complete (nonfiction)" },
      { id: 'm5-4', text: "The reader has what they need before big moments, whether in the story (fiction) or to solve their problem (nonfiction)" }
    ]
  },
  {
    id: 'redundancies',
    title: '6. No Unnecessary Redundancies',
    items: [
      { id: 'm6-1', text: "Repeated ideas verified" },
      { id: 'm6-2', text: "Repeated scenes or emotional beats verified" },
      { id: 'm6-3', text: "Repeated theological concepts verified" },
      { id: 'm6-4', text: "Repeated explanations verified" }
    ]
  },
  {
    id: 'pacing',
    title: '7. Pacing Review',
    items: [
      { id: 'm7-1', text: "Strong opening" },
      { id: 'm7-2', text: "Engaging middle" },
      { id: 'm7-3', text: "Ending feels earned and complete" },
      { id: 'm7-4', text: "No long stretches where momentum drops" }
    ]
  }
];

export const assessmentOptions = [
  'Structure is solid; only minor adjustments needed',
  'Mostly solid; a few chapters need reshaping',
  'Some major rearranging needed',
  'Structure needs re-mapping',
  'I need help evaluating the structure (coaching/editing)'
];


// PASS 2: SENTENCE EDIT (MICRO)

export const fillerWordsData = [
  {
    title: 'Common Filler Words',
    items: ['really', 'very', 'actually', 'basically', 'literally', 'totally', 'kind of', 'sort of', 'maybe', 'perhaps', 'somewhat', 'rather']
  },
  {
    title: 'Weak Verbs & Helper Verbs',
    items: ['was / were / is / are / been', 'has been / had been', 'began to / started to', 'continued to', 'decided to']
  },
  {
    title: 'Overused Writer Crutches',
    items: ['just', 'even', 'still', 'again', 'then', 'suddenly', 'finally']
  },
  {
    title: 'Redundant Phrases',
    items: ['absolutely essential → essential', 'close proximity → proximity', 'end result → result', 'in order to → to', 'whether or not → whether']
  },
  {
    title: 'Theological Redundancies (Nonfiction)',
    items: ['in my opinion', 'I believe that…', 'the point I’m trying to make', 'the truth is that…']
  }
];

export const clarityCompassData = [
  {
    title: 'A. Clarity',
    items: ['Is this sentence as clear as it could be?', 'Can I say this in fewer words?', 'Am I using precise nouns and strong verbs?']
  },
  {
    title: 'B. Readability',
    items: ['Do sentence lengths vary?', 'Are long sentences broken up when needed?', 'Is there any breathlessness (too many clauses)?']
  },
  {
    title: 'C. Flow',
    items: ['Does this idea connect logically to the previous one?', 'Are transitions natural and smooth?', 'Is there any jarring jump or tangent?']
  },
  {
    title: 'D. Tone & Voice',
    items: ['Is the tone consistent with the rest of the chapter?', 'Does this sentence sound like my true voice?', 'Is anything too harsh, preachy, or flat?']
  },
  {
    title: 'E. Redundancy',
    items: ['Am I repeating myself unnecessarily?', 'Is this the third time I’ve made this point?']
  }
];

export const optionalToolsData = [
  {
    id: 'opt_paragraphs',
    title: 'Paragraph Flow Finder',
    description: 'Check paragraphs for:',
    items: [
      'One main idea each',
      'Smooth transitions to next paragraph',
      'No sudden logic leaps',
      'No overly dense \'brick wall\' paragraphs'
    ],
    prompt: 'Paragraphs that need to be split or clarified:'
  },
  {
    id: 'opt_readaloud',
    title: 'Read-Aloud Check (Preview to Lesson 4)',
    description: 'As you whisper-read sections to yourself:',
    items: [
      'Where did you stumble?',
      'What felt awkward?',
      'Which sentences were difficult to say aloud?'
    ],
    prompt: 'Notes from preview read-aloud:'
  }
];

export const dialogueTightenerData = {
  id: 'opt_dialogue',
  title: 'Dialogue / Explanation Tightener',
  prompt: 'Dialogue/explanation areas to tighten:',
  subsets: [
    {
      title: 'Fiction:',
      items: ['Dialogue serves a purpose', 'Avoid "on-the-nose" exposition', 'Vary tags and beats', 'Dialogue emotionally matches the scene']
    },
    {
      title: 'Nonfiction:',
      items: ['Explanations are crisp and actionable', 'Avoid long, meandering commentary', 'Teaching steps flow clearly']
    }
  ]
};